import {groupSettingsErrorKey} from 'app/services/operation-conflict';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {
	CurrencyCode,
	DepartmentPayrollCurrencyUpdateResponse,
	normalizeCurrencyCode,
	PAYROLL_CURRENCY_CODES,
	trackByCurrencyCode
} from '../../../models/payroll-currency.model';
import { HTTP_STATUS_CODES } from '../../../shared.constants/all-constants';
import { MORNING_OVERTIME_I18N_KEYS } from '../../common/morning-overtime-readiness/constants/morning-overtime-readiness.constants';
import { MorningOvertimeHeadSetting } from '../../common/morning-overtime-readiness/models/morning-overtime-readiness.model';
import {
	VIEW_GROUP_EMPTY_TEXT,
	VIEW_GROUP_PAYROLL_CURRENCY_I18N_KEYS
} from './view-group.constants';
import { ViewGroupNotificationPort } from './view-group-notification.port';
import { ViewGroupIdentityState } from './view-group-identity.state';
import { ViewGroupSettingsApi } from './view-group-settings.api';
import { DepartmentPayrollCurrencyUpdate } from './view-group.types';

@Injectable()
export class ViewGroupSettingsCoordinator implements OnDestroy {
	readonly payrollCurrencies = PAYROLL_CURRENCY_CODES;
	readonly trackByPayrollCurrency = trackByCurrencyCode;
	morningOvertimeSetting: MorningOvertimeHeadSetting = null;
	morningOvertimeErrorKey = VIEW_GROUP_EMPTY_TEXT;
	morningOvertimeLoading = false;
	morningOvertimeSaving = false;
	payrollCurrencySaving = false;
	payrollCurrencyRequiresReload = false;
	payrollCurrencyErrorKey = VIEW_GROUP_EMPTY_TEXT;
	private payrollCurrencyDraft: CurrencyCode = null;
	private readonly currencyRequestChanged$ = new Subject<void>();

	private morningOvertimeRequestedGroupId: string | null = null;
	private readonly destroy$ = new Subject<void>();

	constructor(
		private settingsApi: ViewGroupSettingsApi,
		private notifications: ViewGroupNotificationPort,
		private identity: ViewGroupIdentityState
	) {}

	get canShowMorningOvertime(): boolean {
		return this.canManageMorningOvertime() &&
			(this.morningOvertimeLoading || this.hasCurrentMorningOvertimeSetting() ||
			!!this.morningOvertimeErrorKey);
	}

	get canManagePayrollCurrency(): boolean {
		return this.identity.hasCurrentGroupContext && this.identity.groupContext.canManagePayrollCurrency;
	}

	get currentPayrollCurrency(): CurrencyCode {
		return this.payrollCurrencyDraft || normalizeCurrencyCode(
			this.identity.group ? this.identity.group.payrollCurrency : undefined
		);
	}

	loadMorningOvertime(): void {
		this.resetMorningOvertime();
		if (!this.canManageMorningOvertime()) {
			return;
		}
		const groupId = this.currentGroupId();
		if (!groupId) {
			return;
		}
		this.morningOvertimeRequestedGroupId = groupId;
		this.morningOvertimeLoading = true;
		this.requestMorningOvertime(groupId);
	}

	changeMorningOvertime(targetEnabled: boolean): void {
		if (!this.canChangeMorningOvertime()) {
			return;
		}
		this.updateMorningOvertime(targetEnabled);
	}

	resetMorningOvertime(): void {
		this.morningOvertimeSetting = null;
		this.morningOvertimeErrorKey = VIEW_GROUP_EMPTY_TEXT;
		this.morningOvertimeLoading = false;
		this.morningOvertimeSaving = false;
		this.morningOvertimeRequestedGroupId = null;
	}

	changePayrollCurrency(
		payrollCurrency: string,
		selectElement?: HTMLSelectElement
	): void {
		const requestedCurrency = normalizeCurrencyCode(payrollCurrency);
		const currentCurrency = this.currentPayrollCurrency;
		if (!this.canChangePayrollCurrency(requestedCurrency, currentCurrency)) {
			this.restorePayrollCurrency(selectElement, currentCurrency);
			return;
		}
		const update = {
			groupId: this.identity.group.id,
			requestedCurrency: requestedCurrency
		};
		this.payrollCurrencyDraft = requestedCurrency;
		this.requestPayrollCurrency(update);
	}

	cancelPayrollCurrency(): void {
		this.currencyRequestChanged$.next();
		this.payrollCurrencySaving = false;
	}

	acceptPayrollCurrencyContext(): void {
		this.payrollCurrencyDraft = null;
		this.payrollCurrencyRequiresReload = false;
		this.payrollCurrencyErrorKey = VIEW_GROUP_EMPTY_TEXT;
	}

	ngOnDestroy(): void {
		this.currencyRequestChanged$.complete();
		this.destroy$.next();
		this.destroy$.complete();
	}

	private requestMorningOvertime(groupId: string): void {
		this.settingsApi.loadMorningOvertime(groupId)
			.takeUntil(this.destroy$)
			.subscribe(
				setting => this.applyMorningOvertime(setting, groupId),
				error => this.handleMorningOvertimeLoadError(error, groupId)
			);
	}

	private applyMorningOvertime(
		setting: MorningOvertimeHeadSetting,
		groupId: string
	): void {
		if (!this.isCurrentMorningOvertimeRequest(groupId)) {
			return;
		}
		this.morningOvertimeLoading = false;
		this.morningOvertimeRequestedGroupId = null;
		if (!this.morningOvertimeMatchesGroup(setting, groupId)) {
			this.resetMorningOvertime();
			return;
		}
		this.morningOvertimeSetting = setting;
		this.morningOvertimeErrorKey = VIEW_GROUP_EMPTY_TEXT;
	}

	private handleMorningOvertimeLoadError(
		error: HttpErrorResponse,
		groupId: string
	): void {
		if (!this.isCurrentMorningOvertimeRequest(groupId)) {
			return;
		}
		this.morningOvertimeLoading = false;
		this.morningOvertimeRequestedGroupId = null;
		this.morningOvertimeSetting = null;
		if (error && error.status === HTTP_STATUS_CODES.FORBIDDEN) {
			return;
		}
		this.morningOvertimeErrorKey = MORNING_OVERTIME_I18N_KEYS.headActionError;
	}

	private updateMorningOvertime(targetEnabled: boolean): void {
		const groupId = this.morningOvertimeSetting.mainGroupId;
		const previousSetting = this.morningOvertimeSetting;
		this.morningOvertimeSetting = this.optimisticMorningOvertime(
			previousSetting,
			targetEnabled
		);
		this.morningOvertimeErrorKey = VIEW_GROUP_EMPTY_TEXT;
		this.morningOvertimeSaving = true;
		this.settingsApi.updateMorningOvertime(groupId, { enabled: targetEnabled })
			.takeUntil(this.destroy$)
			.subscribe(
				setting => this.handleMorningOvertimeUpdated(setting, groupId),
				error => this.handleMorningOvertimeUpdateError(error, groupId, previousSetting)
			);
	}

	private handleMorningOvertimeUpdated(
		setting: MorningOvertimeHeadSetting,
		groupId: string
	): void {
		if (!this.isCurrentGroup(groupId)) {
			return;
		}
		this.morningOvertimeSaving = false;
		if (!this.morningOvertimeMatchesGroup(setting, groupId)) {
			this.resetMorningOvertime();
			return;
		}
		this.morningOvertimeSetting = setting;
		this.morningOvertimeErrorKey = VIEW_GROUP_EMPTY_TEXT;
		this.notifications.success(MORNING_OVERTIME_I18N_KEYS.headSaveSuccess);
	}

	private handleMorningOvertimeUpdateError(
		error: HttpErrorResponse,
		groupId: string,
		previousSetting: MorningOvertimeHeadSetting
	): void {
		if (!this.isCurrentGroup(groupId)) {
			return;
		}
		this.morningOvertimeSaving = false;
		this.morningOvertimeSetting = previousSetting;
		if (error && error.status === HTTP_STATUS_CODES.FORBIDDEN) {
			this.handleForbiddenMorningOvertimeUpdate();
			return;
		}
		this.morningOvertimeErrorKey = VIEW_GROUP_EMPTY_TEXT;
		this.notifications.error(MORNING_OVERTIME_I18N_KEYS.headSaveError);
	}

	private optimisticMorningOvertime(
		setting: MorningOvertimeHeadSetting,
		enabled: boolean
	): MorningOvertimeHeadSetting {
		return {
			mainGroupId: setting.mainGroupId,
			mainGroupName: setting.mainGroupName,
			enabled: enabled,
			canManage: setting.canManage,
			updatedAt: setting.updatedAt,
			updatedByUserId: setting.updatedByUserId
		};
	}

	private handleForbiddenMorningOvertimeUpdate(): void {
		this.morningOvertimeErrorKey = MORNING_OVERTIME_I18N_KEYS.accessDenied;
		this.notifications.error(MORNING_OVERTIME_I18N_KEYS.accessDenied);
	}

	private hasCurrentMorningOvertimeSetting(): boolean {
		return !!this.morningOvertimeSetting &&
			this.morningOvertimeMatchesGroup(
				this.morningOvertimeSetting,
				this.currentGroupId()
			) && this.morningOvertimeSetting.canManage;
	}

	private canChangeMorningOvertime(): boolean {
		return this.canManageMorningOvertime() && !!this.morningOvertimeSetting &&
			this.morningOvertimeSetting.canManage && !this.morningOvertimeSaving &&
			this.morningOvertimeMatchesGroup(
				this.morningOvertimeSetting,
				this.currentGroupId()
			);
	}

	private isCurrentMorningOvertimeRequest(groupId: string): boolean {
		return this.morningOvertimeRequestedGroupId === groupId &&
			this.isCurrentGroup(groupId) && this.canManageMorningOvertime();
	}

	private morningOvertimeMatchesGroup(
		setting: MorningOvertimeHeadSetting,
		groupId: string | null
	): boolean {
		return !!setting && !!groupId && setting.mainGroupId === groupId;
	}

	private canChangePayrollCurrency(
		requested: CurrencyCode,
		current: CurrencyCode
	): boolean {
		return this.canManagePayrollCurrency && !this.payrollCurrencySaving && !this.payrollCurrencyRequiresReload &&
			requested !== current;
	}

	private requestPayrollCurrency(update: DepartmentPayrollCurrencyUpdate): void {
		this.payrollCurrencySaving = true;
		this.settingsApi.updatePayrollCurrency(update.groupId, update.requestedCurrency)
			.takeUntil(this.currencyRequestChanged$).takeUntil(this.destroy$)
			.subscribe(
				response => this.handlePayrollCurrencySaved(update, response),
				error => this.handlePayrollCurrencyFailed(update, error)
			);
	}

	private handlePayrollCurrencySaved(
		update: DepartmentPayrollCurrencyUpdate,
		response: HttpResponse<DepartmentPayrollCurrencyUpdateResponse>
	): void {
		this.payrollCurrencySaving = false;
		if (!this.isCurrentGroup(update.groupId)) {
			return;
		}
		const body = response.body;
		if (response.status !== HTTP_STATUS_CODES.OK || !body || body.id !== update.groupId ||
			!PAYROLL_CURRENCY_CODES.some(currency => currency === body.payrollCurrency)) {
			this.handlePayrollCurrencyFailed(update); return;
		}
		this.identity.group.payrollCurrency = body.payrollCurrency;
		this.acceptPayrollCurrencyContext();
		this.notifications.success(VIEW_GROUP_PAYROLL_CURRENCY_I18N_KEYS.UPDATED);
	}

	private handlePayrollCurrencyFailed(update: DepartmentPayrollCurrencyUpdate, error?: HttpErrorResponse): void {
		this.payrollCurrencySaving = false;
		if (!this.isCurrentGroup(update.groupId)) {
			return;
		}
		this.payrollCurrencyRequiresReload = true;
		this.payrollCurrencyErrorKey = groupSettingsErrorKey(error);
		this.notifications.error(this.payrollCurrencyErrorKey);
	}

	private restorePayrollCurrency(
		element: HTMLSelectElement | undefined,
		currency: CurrencyCode
	): void {
		if (element) {
			element.value = currency;
		}
	}

	private currentGroupId(): string | null {
		return this.identity.group && this.identity.group.id
			? this.identity.group.id.toString()
			: null;
	}

	private isCurrentGroup(groupId: string): boolean {
		return !!groupId && this.currentGroupId() === groupId;
	}

	private canManageMorningOvertime(): boolean {
		return this.identity.hasCurrentGroupContext && this.identity.groupContext.canManageMorningOvertime &&
			!this.identity.groupContext.parentDepartment && !this.identity.group.parentDepartmentId &&
			!!this.identity.group.head && !!this.identity.user &&
			this.identity.group.head.id === this.identity.user.id;
	}
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/view-group-settings.coordinator.ts